import { createContext, useContext, useEffect, useState } from "react"
import type { ReactNode } from "react"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"

export type Theme = "light" | "dark"

interface ThemeContextValue {
  theme: Theme
  setTheme: (t: Theme) => void
  toggle: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const STORAGE_KEY = "wgt_theme"

/**
 * Per-route default themes. Admin (/_authed/admin/...) defaults to light:
 * denser data UI, easier to read in offices. Everything else inherits the
 * broader app default (light).
 *
 * User preference (saved to localStorage) always wins over these defaults.
 */
function defaultThemeForPath(path: string): Theme {
  if (path.startsWith("/_authed/admin") || path.startsWith("/admin")) {
    return "light"
  }
  return "light"
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Hydrate from localStorage if available, else fall back to route default.
  const [theme, setThemeState] = useState<Theme>("light")
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null
    if (stored === "light" || stored === "dark") {
      setThemeState(stored)
    } else {
      setThemeState(defaultThemeForPath(window.location.pathname))
    }
    setHydrated(true)
  }, [])

  // Apply theme to <html> via data-theme so CSS vars switch.
  useEffect(() => {
    if (!hydrated) return
    document.documentElement.setAttribute("data-theme", theme)
  }, [theme, hydrated])

  function setTheme(next: Theme) {
    setThemeState(next)
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, next)
    }
  }
  function toggle() {
    setTheme(theme === "light" ? "dark" : "light")
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    // Outside a provider — return a no-op shape so leaf components don't
    // crash during SSR or in isolated tests.
    return {
      theme: "light",
      setTheme: () => {},
      toggle: () => {},
    }
  }
  return ctx
}

/**
 * Sun/moon toggle. The icon swap is animated — the outgoing icon rotates
 * out + scales down while the incoming one rotates in. Falls back to a
 * straight cross-fade for users with reduced-motion.
 */
export function ThemeToggle({ className = "" }: { className?: string }) {
  const { theme, toggle } = useTheme()
  const reduced = useReducedMotion()

  const swapVariants = reduced
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      }
    : {
        initial: { opacity: 0, rotate: -90, scale: 0.6 },
        animate: { opacity: 1, rotate: 0, scale: 1 },
        exit: { opacity: 0, rotate: 90, scale: 0.6 },
      }

  return (
    <button
      onClick={toggle}
      aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
      title={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
      className={[
        "group relative inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--foreground)] shadow-[inset_0_1px_0_rgba(255,255,255,0.82)] transition-colors hover:border-[color:var(--primary)] hover:bg-[color:var(--surface-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--background)]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={theme}
          variants={swapVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: reduced ? 0.12 : 0.28, ease: [0.4, 0, 0.2, 1] }}
          className="absolute inset-0 flex items-center justify-center"
        >
          {theme === "light" ? (
            // Moon — switch to dark
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          ) : (
            // Sun — switch to light
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
            </svg>
          )}
        </motion.span>
      </AnimatePresence>
    </button>
  )
}

/**
 * Brand mark — the existing PNG inside an indigo-tinted frame. The frame
 * picks up a lime-accent ring on hover so it feels alive without animating
 * the artwork itself.
 */
export function WildGuessLogo({
  size = 28,
  className = "",
  framed = false,
}: {
  size?: number
  className?: string
  /** When true, wraps the mark in a brand frame with hover affordance. */
  framed?: boolean
}) {
  if (!framed) {
    return (
      <img
        src="/wild-guess-logo.png"
        alt="Wild Guess Trivia"
        width={size}
        height={size}
        className={["rounded-md", className].filter(Boolean).join(" ")}
      />
    )
  }

  // Framed variant: padded indigo block with a lime ring on hover.
  const pad = Math.max(8, Math.round(size * 0.18))
  return (
    <span
      className={[
        "relative inline-flex items-center justify-center rounded-2xl bg-[color:var(--primary)] text-[color:var(--primary-foreground)] shadow-[0_18px_60px_-20px_rgba(71,71,221,0.55)] ring-1 ring-[color:var(--primary)]/40 transition-all duration-300 hover:ring-[color:var(--accent)] hover:ring-2",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ padding: pad }}
    >
      <img
        src="/wild-guess-logo.png"
        alt="Wild Guess Trivia"
        width={size}
        height={size}
        className="rounded-lg"
      />
    </span>
  )
}
